import { Fragment } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Divider } from '@hivebrite/design-system/Divider';
import { useTheme } from '@hivebrite/design-system/Theme';

import type { SublineItem } from 'hb-builder/components/BuilderComponents/block/Strips/types/items';
import type { StyleChildProps } from 'hb-builder/components/BuilderComponents/block/Strips/types/props';
import ColorSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/ColorSetting';
import FontFamily from 'hb-builder/components/common/SettingsLibrary/CommonSettings/FontFamily';
import RadiosInput from 'hb-builder/components/common/SettingsLibrary/ComposedInputs/RadiosInput';
import InputTitle from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/InputTitle';
import { useBuilderConfigContext } from 'hb-builder/hooks';

import { getColorModeOptions } from '../utils/constants';

interface SublineProps extends StyleChildProps {
  sublines: SublineItem[];
}

const Subline = ({
  item,
  handleBulkChange,
  onChange,
  parentItem,
  sublines,
}: SublineProps) => {
  const {
    features: { color },
    hivebrite: { networkColors, networkTheming },
  } = useBuilderConfigContext();
  const colorSettingsProps = {
    color,
    networkColors,
    networkTheming,
  };
  const theme = useTheme();

  // RadioInput option values must be unique on a page,
  // so we added the name of the item as an identifier
  const optionPrefix = 'subline-';

  return (
    <>
      <FontFamily
        settingKey="textFontFamily"
        item={item}
        onChange={handleBulkChange}
      />
      <Box mb={6}>
        <RadiosInput
          direction="horizontal"
          name={`${optionPrefix}text-color-mode`}
          options={getColorModeOptions({ optionPrefix })}
          onChange={(value) => {
            const finalValue = value.replace(optionPrefix, '');

            onChange({
              id: parentItem.id,
              settingKey: 'attributes.sublineColorMode',
              value: finalValue,
            });
          }}
          value={`${optionPrefix}${
            parentItem.attributes?.sublineColorMode || 'same'
          }`}
        />
      </Box>
      {parentItem.attributes?.sublineColorMode !== 'different' && (
        <>
          <ColorSetting
            settingKey="textColor"
            title={s__('Builder|Text color')}
            transparent={false}
            item={item}
            onChange={handleBulkChange}
            {...colorSettingsProps}
          />
          <ColorSetting
            settingKey="textBackgroundColor"
            title={s__('Builder|Background color')}
            transparent
            item={item}
            onChange={handleBulkChange}
            {...colorSettingsProps}
          />
        </>
      )}
      {parentItem.attributes?.sublineColorMode === 'different' && (
        <Box flexDirection="column">
          {sublines.map((subline, index) => {
            const colorSettingsButtonProps = {
              defaultAttributes: {
                textBackgroundColor: item.attributes?.textBackgroundColor,
                textColor: item.attributes?.textColor || theme.colors.black,
              },
              item: subline,
              onChange,
              ...colorSettingsProps,
            };
            const isLast = sublines.length - 1 === index;

            return (
              <Fragment key={subline.id}>
                <InputTitle
                  boxProps={{ mb: 3 }}
                  id="settingsSublineStripsColorMode"
                  label={s__('Builder|Frame %{number}').i__({
                    number: (index + 1).toString(),
                  })}
                />
                <ColorSetting
                  settingKey="textColor"
                  title={s__('Builder|Text color')}
                  transparent={false}
                  {...colorSettingsButtonProps}
                />
                <ColorSetting
                  settingKey="textBackgroundColor"
                  title={s__('Builder|Background color')}
                  transparent
                  {...colorSettingsButtonProps}
                />
                {!isLast && <Divider m={0} mb={4} mt={1} />}
              </Fragment>
            );
          })}
        </Box>
      )}
    </>
  );
};

export default Subline;
